import React, { useEffect, useState } from "react";
import { SlidersHorizontal, Loader2, Volume2, Mic, Music } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CameraTracks } from "@/components/audio/CameraTracks";
import { useApp } from "@/context/AppContext";
import NoProject from "@/components/NoProject";

const EQ = [
  { v: "none", label: "Flat / None", desc: "No tonal shaping" },
  { v: "voice", label: "Voice Clarity", desc: "Highpass 80Hz + 3kHz presence" },
  { v: "warm", label: "Warm Cinematic", desc: "+200Hz body, −8kHz air" },
  { v: "bright", label: "Bright", desc: "+10kHz sparkle" },
];

export default function AudioMix() {
  const { projectId } = useApp();
  const [mix, setMix] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.getAudioMix(projectId).then(setMix).catch(() => {});
  }, [projectId]);

  if (!projectId) return <NoProject title="Audio Mix needs a project" />;
  if (!mix) return <div className="p-8"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const save = async (patch) => {
    const next = { ...mix, ...patch };
    setMix(next); setSaving(true);
    try { const d = await api.setAudioMix(projectId, patch); setMix(d); toast.success("Mix updated — applies on next render"); }
    catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 fade-up space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#212534] flex items-center justify-center"><SlidersHorizontal size={22} className="text-[#E6B04E]" /></div>
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Audio Mix Studio</h1>
          <p className="text-[#94A3B8] text-sm">Dialogue-priority ducking, EQ &amp; music level — applied during render {saving ? "· saving…" : ""}</p>
        </div>
      </div>

      {projectId && <CameraTracks projectId={projectId} />}

      {/* tracks visual */}
      <div className="studio-card p-5 space-y-3">
        {[["A1 Dialogue", "#00F0FF", Mic], ["A3 Music", "#E6B04E", Music], ["A4 Ambient", "#8B5CF6", Volume2]].map(([n, c, I]) => (
          <div key={n} className="flex items-center gap-3">
            <I size={16} style={{ color: c }} />
            <span className="text-sm w-32">{n}</span>
            <div className="flex-1 h-2 rounded-full bg-[#0c0e14] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: n.includes("Music") ? `${mix.music_volume * 100}%` : "90%", background: c }} />
            </div>
          </div>
        ))}
      </div>

      <div className="studio-card p-6 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-semibold text-sm">Music Volume</label>
            <span className="font-mono text-sm text-[#E6B04E]">{Math.round(mix.music_volume * 100)}%</span>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={mix.music_volume} data-testid="mix-music-volume"
            onChange={(e) => setMix({ ...mix, music_volume: parseFloat(e.target.value) })}
            onMouseUp={(e) => save({ music_volume: parseFloat(e.target.value) })}
            className="w-full accent-[#E6B04E]" />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="font-semibold text-sm">Dialogue-Priority Ducking</label>
            <p className="text-[#64748B] text-xs">Automatically lowers music under speech (sidechain compressor)</p>
          </div>
          <button data-testid="mix-ducking" onClick={() => save({ ducking: !mix.ducking })}
            className={`relative w-11 h-6 rounded-full transition-colors ${mix.ducking ? "bg-[#E6B04E]" : "bg-[#252A3B]"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${mix.ducking ? "translate-x-5" : ""}`} />
          </button>
        </div>

        <div>
          <label className="font-semibold text-sm block mb-2">Dialogue EQ Preset</label>
          <div className="grid grid-cols-2 gap-2">
            {EQ.map((e) => (
              <button key={e.v} data-testid={`mix-eq-${e.v}`} onClick={() => save({ eq: e.v })}
                className={`studio-card p-3 text-left transition-all ${mix.eq === e.v ? "border-[#E6B04E]" : "hover:border-[#3B4259]"}`}>
                <div className="font-semibold text-sm">{e.label}</div>
                <div className="metric-label" style={{ fontSize: 9 }}>{e.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[#64748B] text-xs">Loudness is normalized to −16 LUFS (EBU R128) on the master automatically.</p>
    </div>
  );
}
